import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate, Link } from '@tanstack/react-router';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/auth';

interface DreamFormData {
  rawContent: string;
  dreamType: 'sleeping_dream' | 'future_dream';
  visibility: 'public' | 'private';
  howYouFeel: string;
  yourInterpretation: string;
  notablePeople: string;
  notableObjects: string;
  notableLocations: string;
  stressCode: number;
}

export function NewDreamPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<DreamFormData>({
    rawContent: '',
    dreamType: 'sleeping_dream',
    visibility: 'private',
    howYouFeel: '',
    yourInterpretation: '',
    notablePeople: '',
    notableObjects: '',
    notableLocations: '',
    stressCode: 5,
  });

  const createDream = useMutation({
    mutationFn: () => api.dreams.create(formData),
    onSuccess: (data) => {
      navigate({ to: '/dreams/$dreamId', params: { dreamId: data.id } });
    },
  });

  if (!user) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        <h1 className="text-2xl font-bold text-white mb-4">Sign in to record dreams</h1>
        <Link to="/login" className="btn-primary">Login</Link>
      </div>
    );
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (step === 1 && formData.rawContent.length >= 10) {
      setStep(2);
    } else if (step === 2) {
      createDream.mutate();
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-12">
      <Link to="/dreams" className="text-dream-400 hover:text-white text-sm mb-4 block">
        &larr; Back to dreams
      </Link>

      <h1 className="text-3xl font-display font-bold text-white mb-2">Record Your Dream</h1>
      <p className="text-dream-300 mb-8">
        {step === 1
          ? 'Describe your dream in as much detail as you can remember.'
          : 'Add some additional details to help with interpretation.'}
      </p>

      {/* Progress */}
      <div className="flex gap-2 mb-8">
        <div className={`h-1 flex-1 rounded ${step >= 1 ? 'bg-dream-500' : 'bg-dream-800'}`} />
        <div className={`h-1 flex-1 rounded ${step >= 2 ? 'bg-dream-500' : 'bg-dream-800'}`} />
      </div>

      <form onSubmit={handleSubmit}>
        {step === 1 && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-dream-200 mb-2">
                Your Dream
              </label>
              <textarea
                value={formData.rawContent}
                onChange={(e) => setFormData({ ...formData, rawContent: e.target.value })}
                placeholder="I was walking through a forest when I noticed..."
                rows={10}
                className="input resize-none"
                required
                minLength={10}
              />
              <p className="text-dream-400 text-sm mt-2">
                {formData.rawContent.length} characters (minimum 10)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-dream-200 mb-2">
                Dream Type
              </label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="dreamType"
                    value="sleeping_dream"
                    checked={formData.dreamType === 'sleeping_dream'}
                    onChange={() => setFormData({ ...formData, dreamType: 'sleeping_dream' })}
                    className="text-dream-500"
                  />
                  <span className="text-dream-200">Sleeping Dream</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="dreamType"
                    value="future_dream"
                    checked={formData.dreamType === 'future_dream'}
                    onChange={() => setFormData({ ...formData, dreamType: 'future_dream' })}
                    className="text-dream-500"
                  />
                  <span className="text-dream-200">Future Vision</span>
                </label>
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-dream-200 mb-2">
                How did you feel? (optional)
              </label>
              <input
                type="text"
                value={formData.howYouFeel}
                onChange={(e) => setFormData({ ...formData, howYouFeel: e.target.value })}
                placeholder="Curious, anxious, peaceful..."
                className="input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-dream-200 mb-2">
                Notable people (optional)
              </label>
              <input
                type="text"
                value={formData.notablePeople}
                onChange={(e) => setFormData({ ...formData, notablePeople: e.target.value })}
                placeholder="Mom, a stranger, my old friend..."
                className="input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-dream-200 mb-2">
                Notable places (optional)
              </label>
              <input
                type="text"
                value={formData.notableLocations}
                onChange={(e) => setFormData({ ...formData, notableLocations: e.target.value })}
                placeholder="My childhood home, a beach, a maze..."
                className="input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-dream-200 mb-2">
                Stress level: {formData.stressCode}/10
              </label>
              <input
                type="range"
                min="1"
                max="10"
                value={formData.stressCode}
                onChange={(e) => setFormData({ ...formData, stressCode: parseInt(e.target.value) })}
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-dream-200 mb-2">
                Visibility
              </label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="visibility"
                    value="private"
                    checked={formData.visibility === 'private'}
                    onChange={() => setFormData({ ...formData, visibility: 'private' })}
                    className="text-dream-500"
                  />
                  <span className="text-dream-200">Private</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="visibility"
                    value="public"
                    checked={formData.visibility === 'public'}
                    onChange={() => setFormData({ ...formData, visibility: 'public' })}
                    className="text-dream-500"
                  />
                  <span className="text-dream-200">Share with community</span>
                </label>
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-4 mt-8">
          {step === 2 && (
            <button type="button" onClick={() => setStep(1)} className="btn-secondary">
              Back
            </button>
          )}
          <button
            type="submit"
            disabled={step === 1 && formData.rawContent.length < 10}
            className="btn-primary flex-1"
          >
            {step === 1 ? 'Continue' : createDream.isPending ? 'Saving...' : 'Save & Analyze'}
          </button>
        </div>
      </form>
    </div>
  );
}
